import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdInventory,
  MdLocationOn,
  MdTimer,
  MdSearch,
  MdFilterList,
  MdInfo,
  MdWarning,
  MdTrendingUp,
  MdCalendarToday,
  MdViewModule,
  MdGridOn,
  MdBuild,
  MdClose,
  MdError,
  MdCheckCircle,
  MdPerson,
  MdMessage,
  MdVisibility,
  MdThumbUp,
  MdThumbDown,
  MdAssignment,
  MdPhone,
  MdBusiness
} from "react-icons/md";
import axios from 'axios';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from 'utils/tokenManager';

const FarmerList = () => {
  const [farmers, setFarmers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchFarmers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get authorization header
        const authHeader = tokenManager.getAuthHeader();
        if (!authHeader) {
          throw new Error('Authentication token not found. Please login again.');
        }
        
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/farmers', {
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data && response.data.data) {
          // Transform API data to match component structure
          const transformedData = response.data.data.map(farmer => {
            // Parse preferred_communication if it's a string
            let preferredComm = farmer.preferred_communication;
            if (typeof preferredComm === 'string') {
              try {
                preferredComm = JSON.parse(preferredComm);
              } catch (e) {
                preferredComm = [];
              }
            }
            
            // Parse farms_data if it's a string
            let farmsData = farmer.farms_data;
            if (typeof farmsData === 'string') {
              try {
                farmsData = JSON.parse(farmsData);
              } catch (e) {
                farmsData = null;
              }
            }
            
            // Format phone number
            const formatPhone = (phone) => {
              if (!phone) return 'N/A';
              if (phone.startsWith('+')) return phone;
              if (phone.startsWith('233')) return `+${phone}`;
              if (phone.startsWith('0')) return `+233${phone.substring(1)}`;
              return `+233${phone}`;
            };
            
            return {
            id: farmer.farmer_code,
            name: farmer.full_name,
              phone: formatPhone(farmer.phone_number),
            association: farmer.association_name,
            branch: farmer.branch,
            location: farmer.location,
              physical_address: farmer.physical_address,
            farm_location: farmer.farm_location,
            gps_address: farmer.gps_address,
            id_type: farmer.id_type,
            id_number: farmer.id_number,
              gender: farmer.gender,
              age: farmer.age,
              educational_level: farmer.educational_level,
              is_coop_member: farmer.is_coop_member,
              coop_name: farmer.coop_name,
              coop_location: farmer.coop_location,
              years_of_membership: farmer.years_of_membership,
              producer_type: farmer.producer_type,
              keeps_farm_records: farmer.keeps_farm_records,
              farm_records_type: farmer.farm_records_type,
              other_crops_grown: farmer.other_crops_grown,
              farms_data: farmsData,
            passport_picture: farmer.passport_picture_base64,
            emergency_contact: {
              name: farmer.emergency_name,
              relationship: farmer.emergency_relationship,
                phone: formatPhone(farmer.emergency_phone)
              },
              preferred_communication: Array.isArray(preferredComm) 
                ? preferredComm.join(', ') 
                : preferredComm,
            other_communication: farmer.other_communication,
            registration_date: farmer.created_at,
            updated_at: farmer.updated_at,
            declaration_accepted: farmer.declaration_agreed === 1,
            declaration_date: farmer.declaration_date,
            data_protection_accepted: farmer.data_protection_agreed === 1,
            data_protection_date: farmer.data_protection_date,
            leader: farmer.leader,
            role: farmer.role,
              // Additional farming details
              weed_clearing_method: farmer.weed_clearing_method,
              major_pests_diseases: farmer.major_pests_diseases,
              pest_treatment_method: farmer.pest_treatment_method,
              soil_fertility_maintenance: farmer.soil_fertility_maintenance,
              fertilizer_manure_used: farmer.fertilizer_manure_used,
              has_product_store: farmer.has_product_store === 1,
              has_agrochemical_store: farmer.has_agrochemical_store === 1,
              other_infrastructure: farmer.other_infrastructure,
              inspected_by: farmer.inspected_by,
            // Keep these for backward compatibility if needed
            total_bags: 0,
            total_value: 0,
            price_per_bag: 0,
            contribution_date: farmer.created_at
            };
          });
          setFarmers(transformedData);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error:', err);
        if (err.message.includes('Authentication token')) {
          setError('Authentication required. Please login again.');
        } else {
          setError('Error loading farmers data. Please check your connection.');
        }
        setFarmers([]);
        setLoading(false);
      }
    };
    
    fetchFarmers();
  }, []);

  // Calculate statistics
  const calculateStats = (farmers) => {
    const totalFarmers = farmers.length;
    const totalLeaders = farmers.filter(f => f.leader === 'Yes').length;
    const associationsCount = [...new Set(farmers.map(f => f.association))].length;
    const branchesCount = [...new Set(farmers.map(f => f.branch))].length;

    return {
      totalFarmers,
      totalLeaders,
      associationsCount,
      branchesCount
    };
  };

  // Filter and sort farmers
  const filteredFarmers = farmers
    .filter(farmer => {
      const farmerYear = new Date(farmer.contribution_date).getFullYear();
      const matchesSearch = 
        farmer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        farmer.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        farmer.association.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesAssociation = !selectedAssociation || farmer.association === selectedAssociation;
      const matchesBranch = !selectedBranch || farmer.branch === selectedBranch;
      const matchesYear = selectedYear === 'all' || farmerYear === parseInt(selectedYear);
      return matchesSearch && matchesAssociation && matchesBranch && matchesYear;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'date') return new Date(b.registration_date) - new Date(a.registration_date);
      if (sortBy === 'association') return a.association.localeCompare(b.association);
      if (sortBy === 'branch') return a.branch.localeCompare(b.branch);
      return 0;
    });

  const stats = calculateStats(filteredFarmers);

  // Get unique associations and branches for filters
  const associations = [...new Set(farmers.map(f => f.association))];
  const branches = [...new Set(farmers.map(f => f.branch))];

  // Pagination
  const totalPages = Math.ceil(filteredFarmers.length / itemsPerPage);
  const paginatedFarmers = filteredFarmers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <UsibrasLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-5xl text-red-500">
            <MdError className="mx-auto h-16 w-16" />
          </div>
          <p className="text-xl font-semibold text-gray-700 dark:text-white">
            Oops! Something went wrong
          </p>
          <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 h-full">
      {/* Toast Notification */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          position="top-right"
          duration={3000}
        />
      )}

      <Card extra={"w-full h-full p-4"}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Farmers List {selectedYear !== 'all' ? `- ${selectedYear}` : '- All Time'}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            View and manage cashew farmers and their contributions {selectedYear !== 'all' ? `for ${selectedYear}` : 'across all years'}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdPerson className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Farmers</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.totalFarmers}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                <MdAssignment className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Leaders</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.totalLeaders}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                <MdBusiness className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Associations</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.associationsCount}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-yellow-50 p-3 dark:bg-navy-700">
                <MdLocationOn className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Branches</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.branchesCount}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters and Search */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Filters</h3>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedAssociation('');
                setSelectedBranch('');
                setSelectedYear('all');
                setSortBy('date');
              }}
              className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              <MdFilterList className="h-4 w-4" />
              Clear All Filters
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="flex items-center gap-2">
              <MdSearch className="h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search farmers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              />
            </div>
            <div className="flex items-center gap-2">
              <MdBusiness className="h-5 w-5 text-gray-500" />
              <select
                value={selectedAssociation}
                onChange={(e) => setSelectedAssociation(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Associations</option>
                {associations.map(association => (
                  <option key={association} value={association}>{association}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <MdLocationOn className="h-5 w-5 text-gray-500" />
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Branches</option>
                {branches.map(branch => (
                  <option key={branch} value={branch}>{branch}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <MdCalendarToday className="h-5 w-5 text-gray-500" />
              <div className="relative w-full">
                <input
                  type="date"
                  value={selectedYear === 'all' ? '' : `${selectedYear}-01-01`}
                  onChange={(e) => {
                    if (e.target.value) {
                      setSelectedYear(new Date(e.target.value).getFullYear().toString());
                    } else {
                      setSelectedYear('all');
                    }
                  }}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                  placeholder="Select year"
                />
                {selectedYear !== 'all' && (
                  <button
                    onClick={() => setSelectedYear('all')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Show all years"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MdFilterList className="h-5 w-5 text-gray-500" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
                <option value="association">Sort by Association</option>
                <option value="branch">Sort by Branch</option>
              </select>
            </div>
          </div>
        </div>

        {/* Farmers Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-navy-700">
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Farmer Code</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Name</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Association</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Branch</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Role</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Contact</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedFarmers.map((farmer) => (
                <tr key={farmer.id} className="border-b border-gray-200 dark:border-navy-700">
                  <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                    {farmer.id}
                  </td>
                  <td className="py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <MdPerson className="h-4 w-4 text-blue-500" />
                      <span className="font-medium text-navy-700 dark:text-white">{farmer.name}</span>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdBusiness className="h-4 w-4 text-green-500" />
                      <span>{farmer.association}</span>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdLocationOn className="h-4 w-4 text-blue-500" />
                      <span>{farmer.branch}</span>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    {farmer.leader === 'Yes' && farmer.role ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        <MdAssignment className="h-3 w-3" />
                        {farmer.role}
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">Member</span>
                    )}
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdPhone className="h-4 w-4 text-green-500" />
                      <a href={`tel:${farmer.phone}`} className="text-green-600 dark:text-green-400 hover:underline">
                        {farmer.phone}
                      </a>
                    </div>
                  </td>
                  <td className="py-4">
                    <button
                      onClick={() => {
                        setSelectedFarmer(farmer);
                        setShowDetailModal(true);
                      }}
                      className="inline-flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-2 text-xs font-medium text-white hover:bg-blue-600"
                    >
                      <MdVisibility className="h-3 w-3" />
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {paginatedFarmers.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredFarmers.length)} of {filteredFarmers.length} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>

        {/* No Results Message */}
        {filteredFarmers.length === 0 && (
          <div className="mt-8 text-center">
            <MdInfo className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-lg font-medium text-gray-600 dark:text-gray-400">
              No farmers found matching your criteria
            </p>
          </div>
        )}
      </Card>

      {/* Farmer Detail Modal */}
      {showDetailModal && selectedFarmer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
            onClick={() => setShowDetailModal(false)}
          />
          
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto transform rounded-xl bg-white p-3 shadow-2xl transition-all duration-300 ease-in-out dark:bg-navy-800 sm:p-6">
            <div className="mb-4 sm:mb-6 flex items-center justify-between border-b border-gray-200 pb-3 sm:pb-4 dark:border-navy-700">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="rounded-full bg-blue-100 p-1.5 sm:p-2 dark:bg-blue-900">
                  <MdPerson className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                  Farmer Details - {selectedFarmer.name}
                </h3>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="rounded-full p-1 sm:p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-navy-700 dark:hover:text-gray-300"
              >
                <MdClose className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            {/* Farmer Summary */}
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Farmer Code</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.id}</span>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Association</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.association}</span>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Branch</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.branch}</span>
              </div>
              {selectedFarmer.location && (
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Location</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.location}</span>
              </div>
              )}
              {selectedFarmer.leader === 'Yes' && selectedFarmer.role && (
                  <div className="rounded-lg bg-purple-50 p-3 dark:bg-purple-900">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Leadership Role</span>
                  <span className="mt-1 block text-sm font-semibold text-purple-700 dark:text-purple-200">{selectedFarmer.role}</span>
                  </div>
              )}
              {selectedFarmer.producer_type && (
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                  <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Producer Type</span>
                  <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.producer_type}</span>
                </div>
              )}
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Registration Date</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.registration_date ? new Date(selectedFarmer.registration_date).toLocaleDateString() : 'N/A'}</span>
              </div>
              {selectedFarmer.updated_at && (
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                  <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Last Updated</span>
                  <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{new Date(selectedFarmer.updated_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Personal Information */}
            {(selectedFarmer.gender || selectedFarmer.age || selectedFarmer.educational_level || selectedFarmer.physical_address) && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Personal Information</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {selectedFarmer.gender && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Gender</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.gender}</span>
                      </div>
                    )}
                    {selectedFarmer.age && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Age</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.age} years</span>
                      </div>
                    )}
                    {selectedFarmer.educational_level && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Education Level</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.educational_level}</span>
                      </div>
                    )}
                    {selectedFarmer.physical_address && (
                      <div className="sm:col-span-2 lg:col-span-4">
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Physical Address</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.physical_address}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Co-op Membership */}
            {selectedFarmer.is_coop_member === 'YES' && (selectedFarmer.coop_name || selectedFarmer.coop_location) && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Co-operative Membership</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {selectedFarmer.coop_name && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Co-op Name</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.coop_name}</span>
                      </div>
                    )}
                    {selectedFarmer.coop_location && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Co-op Location</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.coop_location}</span>
                      </div>
                    )}
                    {selectedFarmer.years_of_membership && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Years of Membership</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.years_of_membership} years</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ID Information */}
            {selectedFarmer.id_type && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Identification Details</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">ID Type</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.id_type}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">ID Number</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.id_number}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">GPS Address</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.gps_address || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Farm Location */}
            {selectedFarmer.farm_location && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Farm Location</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-green-100 p-2 dark:bg-green-900">
                      <MdLocationOn className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Farm Community</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.farm_location}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Farm Records */}
            {selectedFarmer.keeps_farm_records && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Farm Records</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Keeps Records</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.keeps_farm_records}</span>
                    </div>
                    {selectedFarmer.farm_records_type && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Record Type</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.farm_records_type}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Farms Data */}
            {selectedFarmer.farms_data && Array.isArray(selectedFarmer.farms_data) && selectedFarmer.farms_data.some(farm => farm.location || farm.age || farm.size || farm.yield) && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Cashew Farms</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700 overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-navy-600">
                        <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Farm</th>
                        <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Location</th>
                        <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Age (years)</th>
                        <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Size (acres)</th>
                        <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Yield (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedFarmer.farms_data.map((farm, index) => {
                        if (!farm.location && !farm.age && !farm.size && !farm.yield) return null;
                        return (
                          <tr key={index} className="border-b border-gray-100 dark:border-navy-600">
                            <td className="py-2 text-sm font-medium text-navy-700 dark:text-white">Farm {index + 1}</td>
                            <td className="py-2 text-sm text-gray-600 dark:text-gray-300">{farm.location || '-'}</td>
                            <td className="py-2 text-sm text-gray-600 dark:text-gray-300">{farm.age || '-'}</td>
                            <td className="py-2 text-sm text-gray-600 dark:text-gray-300">{farm.size || '-'}</td>
                            <td className="py-2 text-sm text-gray-600 dark:text-gray-300">{farm.yield || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Crops Grown */}
            {selectedFarmer.other_crops_grown && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Other Crops Grown</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <p className="text-sm text-gray-700 dark:text-gray-300">{selectedFarmer.other_crops_grown}</p>
                </div>
              </div>
            )}

            {/* Infrastructure */}
            {(selectedFarmer.has_product_store || selectedFarmer.has_agrochemical_store || selectedFarmer.other_infrastructure) && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Infrastructure</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="space-y-2">
                    {selectedFarmer.has_product_store && (
                      <div className="flex items-center gap-2">
                        <MdCheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Has Product Store</span>
                      </div>
                    )}
                    {selectedFarmer.has_agrochemical_store && (
                      <div className="flex items-center gap-2">
                        <MdCheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Has Agrochemical Store</span>
                      </div>
                    )}
                    {selectedFarmer.other_infrastructure && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-navy-600">
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Other Infrastructure</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{selectedFarmer.other_infrastructure}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Inspector Information */}
            {selectedFarmer.inspected_by && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Inspector Information</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-900">
                      <MdAssignment className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Inspected By</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.inspected_by}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="mb-6">
              <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Contact Information</h4>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-800">
                      <MdPhone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-navy-700 dark:text-white">Phone Number</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{selectedFarmer.phone}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => window.open(`tel:${selectedFarmer.phone}`, '_self')}
                    className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors"
                  >
                    Call
                  </button>
                </div>
                {selectedFarmer.preferred_communication && (
                  <div className="pt-4 border-t border-gray-200 dark:border-navy-600">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Preferred Communication</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      <MdMessage className="h-3 w-3" />
                      {selectedFarmer.preferred_communication}
                    </span>
                  </div>
                )}
                {selectedFarmer.other_communication && (
                  <div className="pt-4 border-t border-gray-200 dark:border-navy-600 mt-4">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Additional Communication Notes</span>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{selectedFarmer.other_communication}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Emergency Contact */}
            {selectedFarmer.emergency_contact && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Emergency Contact</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Name</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.emergency_contact.name}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Relationship</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedFarmer.emergency_contact.relationship}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Phone Number</span>
                      <a href={`tel:${selectedFarmer.emergency_contact.phone}`} className="mt-1 block text-sm font-semibold text-blue-600 hover:underline dark:text-blue-400">
                        {selectedFarmer.emergency_contact.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Declaration and Data Protection Status */}
            <div className="mb-6">
              <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Consent & Agreements</h4>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Declaration Agreed</span>
                    <div className="mt-1 flex items-center gap-2">
                      {selectedFarmer.declaration_accepted ? (
                        <MdCheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <MdError className="h-5 w-5 text-red-600" />
                      )}
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">
                        {selectedFarmer.declaration_accepted ? 'Yes' : 'No'}
                      </span>
                      {selectedFarmer.declaration_date && (
                        <span className="text-xs text-gray-500">
                          ({new Date(selectedFarmer.declaration_date).toLocaleDateString()})
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Data Protection Agreed</span>
                    <div className="mt-1 flex items-center gap-2">
                      {selectedFarmer.data_protection_accepted ? (
                        <MdCheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <MdError className="h-5 w-5 text-red-600" />
                      )}
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">
                        {selectedFarmer.data_protection_accepted ? 'Yes' : 'No'}
                      </span>
                      {selectedFarmer.data_protection_date && (
                        <span className="text-xs text-gray-500">
                          ({new Date(selectedFarmer.data_protection_date).toLocaleDateString()})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmerList;
